













import { computed, defineComponent, ref } from "@vue/composition-api";
import { InputMessageSearch, Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAdminManagedUser from "@/hooks/useAdminManagedUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";
import MessageGalleryView from "./components/MessageGalleryView.vue";
import { MessageGalleryFilters } from "./types";

export default defineComponent({
  name: "PageMessageGallery",
  components: { MessageGalleryView },
  props: {
    userId: { type: String, required: true },
    threadId: { type: String },
  },
  setup(props, { root }) {
    const { isAdminManagedUser, init } = useAdminManagedUser();

    const loading = ref(true);
    const opponent = ref<UserPublicData>();
    const paymentPackages = ref<PaymentPackage[]>([]);
    const messageThreadId = ref(props.threadId);
    const filter = ref<MessageGalleryFilters>("all");
    const filters = computed(() => {
      if (filter.value === "all") {
        return {};
      }
      if (filter.value === "free") {
        return { isFree: true };
      }
      return {
        isFree: false,
        purchased: filter.value === "purchased",
      };
    });

    const { enableScroll, items: messages, getNextPage, getFirstPage } = usePager<Message, InputMessageSearch>(
      async page =>
        providers.MessagesProvider.find({
          data: {
            threadId: messageThreadId.value,
            hasAttachments: true,
            page,
            sort: "TIME,DESC",
            ...filters.value,
          },
        })
    );

    const getThread = async () => {
      const messageThread = await providers.MessageThreadsProvider.findPersonal({ opponentId: props.userId });
      messageThreadId.value = messageThread.id;
      opponent.value = messageThread.users.find(i => i.id === props.userId);
    };

    const redirect = () => {
      root.$router.replace({ name: "messages" });
    };

    const getOpponentUserInfo = async () => {
      const resp = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      opponent.value = resp.result[0];
    };

    const getPaymentPackages = async () => {
      await providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.MESSAGE_ATTACHMENT] }).then(
        r => (paymentPackages.value = r)
      );
    };

    const handleFilter = async (value: MessageGalleryFilters) => {
      try {
        loading.value = true;
        filter.value = value;
        await getFirstPage();
      } finally {
        loading.value = false;
      }
    };

    const getData = async () => {
      try {
        if (!isAdminManagedUser.value) await init();
        if (!isAdminManagedUser.value) {
          redirect();
          return;
        }
        providers.MessageThreadsProvider.setReceiveErrors(false);
        const promises: Promise<void>[] = [getPaymentPackages()];
        if (!messageThreadId.value) {
          promises.push(getThread());
          await Promise.all(promises);
        } else {
          promises.push(getOpponentUserInfo());
        }
        await Promise.all(promises);
        await Promise.all([getFirstPage()]);
      } catch (e) {
        emitter.emit(EVENTS.STATUS_SHOW, { text: $t(`No Threads with user ${props.userId}`), error: e });
        redirect();
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      messages,
      enableScroll,
      paymentPackages,
      getNextPage,
      opponent,
      handleFilter,
    };
  },
});
